import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

// Import Material UI components
import {
  Box, Typography, Paper, Button, Card, CardContent, Divider, 
  Alert, CircularProgress, Grid, List, ListItem, ListItemIcon, 
  ListItemText, TextField, MenuItem, FormControlLabel, Checkbox,
  Stepper, Step, StepLabel, StepContent
} from '@mui/material';

// Import Material UI icons
import {
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  CreditCard as CreditCardIcon,
  Payment as PaymentIcon,
  ReceiptLong as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';

// Format currency helper
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

const SubscribeToTenantPlan = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  
  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvc: '',
    saveCard: false
  });
  
  // Load plan details
  useEffect(() => {
    const loadPlanDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get(`/api/v1/member-subscription-plans/${planId}`);
        if (response.data && response.data.data) {
          setPlan(response.data.data);
        } else {
          setError("Plan not found or is no longer available");
          toast.error("Plan not found or is no longer available");
        }
      } catch (err) {
        console.error("Failed to load plan details:", err);
        const errorMessage = err.response?.data?.error || 'Error loading subscription plan';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    
    loadPlanDetails();
  }, [planId]);
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setPaymentForm({
      ...paymentForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle step navigation
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };
  
  // Handle subscription submission
  const handleSubscribe = async () => {
    try {
      setProcessing(true);
      setError(null);
      
      // In a real application, we would include Stripe or another payment processor
      // Here we'll simulate a subscription creation
      
      const response = await axios.post('/api/v1/member-subscriptions', {
        planId: plan._id,
        paymentMethod: 'card',
        paymentDetails: {
          // In a real app, you'd use a token from Stripe instead of raw card details
          cardName: paymentForm.cardName
        }
      });
      
      if (response.data && response.data.data) {
        toast.success("Subscription activated successfully!");
        
        // Move to final step which shows success
        setActiveStep(3);
        
        // Redirect to subscription details after a delay
        setTimeout(() => {
          navigate('/member/my-subscription');
        }, 3000);
      }
    } catch (err) {
      console.error("Failed to process subscription:", err);
      const errorMessage = err.response?.data?.error || 'Error processing subscription';
      setError(errorMessage);
      toast.error(errorMessage);
      setProcessing(false);
    }
  };
  
  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Render error state
  if (error && !plan) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/member/subscription-plans')}>
              Back to Plans
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header with navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/member/subscription-plans')}
          sx={{ mr: 2 }}
        >
          Back to Plans
        </Button>
        <Typography variant="h4">
          Subscribe to {plan?.name}
        </Typography>
      </Box>
      
      {/* Error message */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      
      {/* Subscription process */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {/* Step 1: Review plan */}
              <Step>
                <StepLabel>
                  <Typography variant="h6">Review Plan</Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1">
                      You're subscribing to the <strong>{plan?.name}</strong> plan at {formatCurrency(plan?.price)} per {plan?.interval}.
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Please review the plan details before proceeding to payment.
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Continue to Payment
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              
              {/* Step 2: Payment details */}
              <Step>
                <StepLabel>
                  <Typography variant="h6">Payment Information</Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Enter your payment details below. All information is encrypted and secure.
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          name="cardName"
                          label="Name on Card"
                          value={paymentForm.cardName}
                          onChange={handleInputChange}
                          fullWidth
                          margin="normal"
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="cardNumber"
                          label="Card Number"
                          value={paymentForm.cardNumber}
                          onChange={handleInputChange}
                          fullWidth
                          margin="normal"
                          placeholder="1234 5678 9012 3456"
                          required
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          name="expiryDate"
                          label="Expiration Date"
                          value={paymentForm.expiryDate}
                          onChange={handleInputChange}
                          fullWidth
                          margin="normal"
                          placeholder="MM/YY"
                          required
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          name="cvc"
                          label="CVC"
                          value={paymentForm.cvc}
                          onChange={handleInputChange}
                          fullWidth
                          margin="normal"
                          placeholder="123"
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="saveCard"
                              checked={paymentForm.saveCard}
                              onChange={handleInputChange}
                              color="primary"
                            />
                          }
                          label="Save this card for future payments"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                      onClick={handleBack}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{ mt: 1, mr: 1 }}
                      disabled={!paymentForm.cardName || !paymentForm.cardNumber || !paymentForm.expiryDate || !paymentForm.cvc}
                    >
                      Review Order
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              
              {/* Step 3: Confirm subscription */}
              <Step>
                <StepLabel>
                  <Typography variant="h6">Confirm Subscription</Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Please review your subscription details before confirming:
                    </Typography>
                    
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        <strong>Plan:</strong> {plan?.name}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Price:</strong> {formatCurrency(plan?.price)} per {plan?.interval}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Payment Method:</strong> Credit Card (ending in {paymentForm.cardNumber.slice(-4) || 'XXXX'})
                      </Typography>
                      <Typography variant="body2">
                        <strong>Billing:</strong> You will be billed immediately and then on a recurring basis.
                      </Typography>
                    </Paper>
                    
                    <Typography variant="body2" color="text.secondary">
                      By clicking "Subscribe Now", you authorize us to charge your card for this payment and future payments in accordance with our terms.
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                      onClick={handleBack}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubscribe}
                      disabled={processing}
                      startIcon={processing ? <CircularProgress size={20} /> : <CreditCardIcon />}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      {processing ? 'Processing...' : 'Subscribe Now'}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              
              {/* Step 4: Success */}
              <Step>
                <StepLabel>
                  <Typography variant="h6">Subscription Confirmed</Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 3 }}>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <Typography variant="subtitle1">
                        Thank you for your subscription!
                      </Typography>
                    </Alert>
                    <Typography variant="body1">
                      Your subscription to the {plan?.name} plan has been activated successfully.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      You will be redirected to your subscription details page...
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      component={Link}
                      to="/member/my-subscription"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      View My Subscription
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </Paper>
        </Grid>
        
        {/* Plan summary sidebar */}
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ borderRadius: 2, position: 'sticky', top: 20 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShoppingCartIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Order Summary</Typography>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                {plan?.name}
              </Typography>
              <Typography variant="h5" color="primary" gutterBottom>
                {formatCurrency(plan?.price)}
                <Typography component="span" variant="body2" color="text.secondary">
                  /{plan?.interval}
                </Typography>
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {plan?.description}
              </Typography>
              
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Plan Features:
              </Typography>
              
              <List dense disablePadding>
                {(plan?.features || []).map((feature, index) => (
                  <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={feature} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2">{formatCurrency(plan?.price)}</Typography>
              </Box>
              
              {/* Could add taxes, discounts, etc. here */}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', mt: 1 }}>
                <Typography variant="subtitle1">Total:</Typography>
                <Typography variant="subtitle1">{formatCurrency(plan?.price)}</Typography>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                You will be billed on a recurring basis until you cancel your subscription.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubscribeToTenantPlan;
